import { FC, useState } from 'react';
import lodash from 'lodash';
import { Currency } from 'dinero.js';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import DeleteModalForm from 'src/components/Items/DeleteModalForm';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { dineroFormat, fromISOtoFormat, fromJSDateToISO } from 'src/common/utils/dataFormat';
import { IPayOrderFetch } from 'src/models/IOrder';
import Icons from 'src/common/icons';
import './styles.scss';

interface IProps {
  order_id: number;
  isStripeId: boolean;
  payments: IPayOrderFetch[];
}

const typesColors = {
  down_payment: 'green',
  due_payment: 'green',
  manual_payment: 'green',
  old_payment: 'grey',
  refund_payment: 'red',
  afterCharge_payment: 'green',
  pending_payment: 'grey',
  host_transfer: 'orange'
};

const PaymentsList: FC<IProps> = ({ order_id, isStripeId, payments }) => {
  const { sentEmail } = useActions();
  const isLoadingPayment = useTypedSelector(state => state.order.isLoading.payment);
  const isLoadingEmail = useTypedSelector(state => state.order.notification.isLoading.email);

  const [selectedPay, setSelectedPay] = useState<number | null>(null);
  const [openDelete, setOpenDelete] = useState(false);

  const sortedPayments = lodash.sortBy(payments, ['id'], ['desc']);

  const handleDelete = (id: number) => (e: any) => {
    e.stopPropagation();
    setSelectedPay(id);
    setOpenDelete(true);
  };

  return (
    <>
      <div className='payment-list'>
        {sortedPayments.map(payment => (
          <div
            className={`payment-list__item ${
              payment.createBy === 'system'
                ? payment.note === 'hold amount pending'
                  ? 'yellow'
                  : typesColors[payment.type as keyof typeof typesColors]
                : payment.createBy === 'admin'
                ? 'admin'
                : 'host'
            }`}
            key={payment.id}
          >
            <div className='flex_CSB'>
              <div
                className={`payment-list__item__note ${
                  payment.createBy !== 'system' && 'black-text'
                }`}
                title={payment.note}
              >
                <div className='payment-list__item__note__container'>
                  <span>
                    {payment.note.indexOf('pi_') > 0 ? payment.note.split('pi_')[0] : payment.note}
                  </span>
                  {payment.note !== 'Card declined. emailed to pay Due' && (
                    <div className='payment-list__item__note__stripe'>
                      <a
                        href={
                          payment.type === 'host_transfer'
                            ? `https://dashboard.stripe.com/connect/transfers/${payment.stripe_id}`
                            : `https://dashboard.stripe.com/payments/${payment.stripe_id}`
                        }
                        target='_blank'
                      >
                        stripe
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className='payment-list__item__actions'>
                {(payment.type === 'due_payment' ||
                  payment.type === 'down_payment' ||
                  payment.type === 'afterCharge_payment' ||
                  (payment.type === 'manual_payment' && isStripeId)) && (
                  <Tooltip title='Send receipt'>
                    <Button
                      className='payment-list__item__actions__receipt'
                      onClick={() =>
                        sentEmail(order_id, 'get receipt', undefined, payment.stripe_id)
                      }
                    >
                      {isLoadingEmail ? <CircularProgress size={15} /> : '@ receipt'}
                    </Button>
                  </Tooltip>
                )}
                {payment.isDelete &&
                  (isLoadingPayment ? (
                    <CircularProgress size={15} />
                  ) : (
                    <div onClick={handleDelete(payment.id)} className='icon_stroke'>
                      <Icons.Delete />
                    </div>
                  ))}
              </div>
            </div>
            <div className='flex_CSB'>
              <div className='payment-list__item__date'>
                {fromISOtoFormat(fromJSDateToISO(payment.created_at), 'ff')}
              </div>
              <div className='payment-list__item__price'>
                {dineroFormat(payment.price, payment.currency as Currency)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <DeleteModalForm
        id={selectedPay!}
        type='payment'
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
      />
    </>
  );
};

export default PaymentsList;
